import { MusicIcon } from './icons/MusicIcon';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSocketStore } from '../stores/socketStore';

interface PowerUpEffect {
  id: string;
  type: string;
  message?: string;
  hint?: string;
  titleHint?: string;
  artistHint?: string;
  year?: string;
  duration?: number;
  pointsStolen?: number;
  pointsLost?: number;
  targetName?: string;
  attackerName?: string;
}

export default function PowerUpEffects() {
  const { socket } = useSocketStore();
  const [effects, setEffects] = useState<PowerUpEffect[]>([]);
  const [hint, setHint] = useState<{ title?: string; artist?: string; year?: string } | null>(null);
  const [isFrozen, setIsFrozen] = useState(false);
  const [hasShield, setHasShield] = useState(false);
  const [hasDouble, setHasDouble] = useState(false);
  
  useEffect(() => {
    if (!socket) return;
    
    const handleEffect = (data: PowerUpEffect) => {
      const id = Date.now().toString();
      const effect = { ...data, id };
      
      setEffects(prev => [...prev, effect]);
      
      // Auto-remove après 4 secondes
      setTimeout(() => {
        setEffects(prev => prev.filter(e => e.id !== id));
      }, 4000);
      
      // Appliquer les effets spéciaux
      switch (data.type) {
        case '50-50':
          setHint(prev => ({ ...prev, title: data.hint }));
          break;
        case 'hint-letter':
          setHint(prev => ({ 
            ...prev, 
            title: data.titleHint, 
            artist: data.artistHint 
          }));
          break;
        case 'hint-year':
          setHint(prev => ({ ...prev, year: data.year }));
          break;
        case 'frozen':
          setIsFrozen(true);
          setTimeout(() => setIsFrozen(false), (data.duration || 5) * 1000);
          break;
        case 'shield':
          setHasShield(true);
          setTimeout(() => setHasShield(false), (data.duration || 120) * 1000);
          break;
        case 'shield-used':
          setHasShield(false);
          break;
        case 'double':
          setHasDouble(true);
          // Le double sera reset au prochain round
          break;
      }
    };
    
    const handleRoundStart = () => {
      setHint(null);
      setHasDouble(false);
    };
    
    socket.on('game:powerUpEffect', handleEffect);
    socket.on('game:roundStart', handleRoundStart);
    
    return () => {
      socket.off('game:powerUpEffect', handleEffect);
      socket.off('game:roundStart', handleRoundStart);
    };
  }, [socket]);
  
  return (
    <>
      {/* Effet de gel (overlay) */}
      <AnimatePresence>
        {isFrozen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-blue-500/30 backdrop-blur-sm z-40 pointer-events-auto flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="text-6xl"
            >
              <MusicIcon name="freeze"/>
            </motion.div>
            <p className="absolute bottom-20 text-white text-xl font-bold">
              Vous êtes gelé !
            </p>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Indicateurs d'effets actifs */}
      <div className="fixed top-20 left-4 z-30 flex flex-col gap-2">
        <AnimatePresence>
          {hasShield && (
            <motion.div
              initial={{ x: -100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -100, opacity: 0 }}
              className="px-3 py-1 bg-blue-500/20 border border-blue-500/50 rounded-full flex items-center gap-2"
            >
              <MusicIcon name="shield"/>
              <span className="text-sm text-blue-400">Bouclier actif</span>
            </motion.div>
          )}
          {hasDouble && (
            <motion.div
              initial={{ x: -100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -100, opacity: 0 }}
              className="px-3 py-1 bg-yellow-500/20 border border-yellow-500/50 rounded-full flex items-center gap-2"
            >
              <MusicIcon name="boost"/>
              <span className="text-sm text-yellow-400">Double points</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Indices */}
      <AnimatePresence>
        {hint && (hint.title || hint.artist || hint.year) && (
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            className="fixed bottom-32 left-1/2 -translate-x-1/2 z-30 bg-dark-800/95 border border-primary-500/50 rounded-xl p-4 shadow-lg"
          >
            <p className="text-sm text-primary-400 font-bold mb-2"><MusicIcon name="hint"/> Indices</p>
            <div className="space-y-1 text-white">
              {hint.title && (
                <p>
                  <span className="text-dark-400">Titre: </span>
                  <span className="font-mono">{hint.title}</span>
                </p>
              )}
              {hint.artist && (
                <p>
                  <span className="text-dark-400">Artiste: </span>
                  <span className="font-mono">{hint.artist}</span>
                </p>
              )}
              {hint.year && (
                <p>
                  <span className="text-dark-400">Année: </span>
                  <span className="font-bold">{hint.year}</span>
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Notifications de power-ups */}
      <div className="fixed top-20 right-4 z-30 flex flex-col gap-2 max-w-xs">
        <AnimatePresence>
          {effects.map(effect => (
            <motion.div
              key={effect.id}
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 100, opacity: 0 }}
              className={`px-4 py-2 rounded-lg shadow-lg ${getEffectStyle(effect.type)}`}
            >
              <MusicIcon name={effect.type}/> {getEffectMessage(effect)}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </>
  );
}

function getEffectStyle(type: string): string {
  switch (type) {
    case 'frozen':
      return 'bg-blue-500/90 text-white';
    case 'stolen-from':
      return 'bg-red-500/90 text-white';
    case 'steal':
    case 'steal-blocked':
      return 'bg-purple-500/90 text-white';
    case 'shield':
    case 'shield-used':
      return 'bg-blue-400/90 text-white';
    case 'double':
      return 'bg-yellow-500/90 text-black';
    case 'freeze-success':
      return 'bg-cyan-500/90 text-white';
    default:
      return 'bg-dark-700/95 text-white border border-dark-500';
  }
}

function getEffectMessage(effect: PowerUpEffect): string {
  switch (effect.type) {
    case 'frozen':
      return `${effect.attackerName} vous a gelé !`;
    case 'stolen-from':
      return `${effect.attackerName} vous a volé ${effect.pointsLost} points !`;
    case 'steal':
      return `Vous avez volé ${effect.pointsStolen} points à ${effect.targetName} !`;
    case 'steal-blocked':
      return `${effect.message || 'Vol bloqué !'}`;
    case 'shield':
      return `${effect.message || 'Bouclier activé !'}`;
    case 'shield-used':
      return `${effect.message || 'Bouclier utilisé !'}`;
    case 'double':
      return `${effect.message || 'Double points !'}`;
    case 'freeze-success':
      return `${effect.targetName} est gelé !`;
    default:
      return effect.message || 'Power-up utilisé !';
  }
}

// Export pour utiliser isFrozen dans GamePage
export function usePowerUpEffects() {
  const { socket } = useSocketStore();
  const [isFrozen, setIsFrozen] = useState(false);
  
  useEffect(() => {
    if (!socket) return;
    
    const handleEffect = (data: any) => {
      if (data.type === 'frozen') {
        setIsFrozen(true);
        setTimeout(() => setIsFrozen(false), (data.duration || 5) * 1000);
      }
    };
    
    socket.on('game:powerUpEffect', handleEffect);
    
    return () => {
      socket.off('game:powerUpEffect', handleEffect);
    };
  }, [socket]);
  
  return { isFrozen };
}
